import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowUp, X, Mic, Square, Paperclip, Copy, FileText, Wand2, Save, Check, ScrollText, Library, Coins, Sparkles, RefreshCw, Image as ImageIcon, Clapperboard } from "lucide-react";
import { api } from "@/api/client";
import { pipelinesApi } from "@/api/mist";
import { useSession } from "@/context/SessionContext";
import VaultPicker from "@/components/chat/VaultPicker";
import PlansPanel from "@/components/billing/PlansPanel";
import { LedgerAlertHeading } from "@/components/ledger/LedgerAlertHeading";

const SESSION_KEY = "alix_session_id";
const IMG_EXT_RE = /\.(png|jpe?g|webp|heic|gif)$/i;
const ALLOWED = ["jpg", "jpeg", "png", "webp", "pdf", "txt", "heic",
  "mp4", "mov", "webm", "m4v", "mp3", "wav", "m4a", "aac", "ogg", "flac"];
const VIDEO_EXT = ["mp4", "mov", "webm", "m4v"];
const AUDIO_EXT = ["mp3", "wav", "m4a", "aac", "ogg", "flac"];
const sizeLimit = (ext) => VIDEO_EXT.includes(ext) ? 50 * 1024 * 1024 : AUDIO_EXT.includes(ext) ? 24 * 1024 * 1024 : 10 * 1024 * 1024;

// Loads image/video artifact media via an authed blob (artifacts are already
// persisted to the Vault, so this is a preview).
function ArtifactMedia({ art }) {
  const [src, setSrc] = useState(null);
  const path = (art.url || "").replace(/^\/api/, "");
  useEffect(() => {
    let obj;
    if (path) {
      api.get(path, { responseType: "blob" })
        .then((r) => { obj = URL.createObjectURL(r.data); setSrc(obj); })
        .catch(() => {});
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  if (!src) return <div className="w-full h-40 grid place-items-center bg-black/40"><Loader2 size={16} className="text-gold animate-spin" /></div>;
  return art.kind === "video"
    ? <video src={src} controls playsInline data-testid="alix-artifact-video" className="w-full max-h-72 bg-black" />
    : <img src={src} alt={art.title} data-testid="alix-artifact-image" className="w-full object-contain max-h-72 bg-black" />;
}

// Founder Director Mode: polls a background 30s-reel job and, when done, plays
// the finished video inline (it's already auto-saved to the Vault).
function VideoJobArtifact({ job }) {
  const [status, setStatus] = useState(job.status || "processing");
  const [assetId, setAssetId] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!job.job_id) return;
    let alive = true, tries = 0;
    const tick = async () => {
      if (!alive) return;
      tries += 1;
      try {
        const { data } = await api.get(`/pipelines/director/job/${job.job_id}`);
        if (!alive) return;
        if (data.status === "completed" && data.asset_id) { setAssetId(data.asset_id); setStatus("completed"); return; }
        if (data.status === "failed") { setErr(data.error || "render failed"); setStatus("failed"); return; }
      } catch (e) { /* transient */ }
      if (tries < 90) setTimeout(tick, 5000);
      else { setStatus("failed"); setErr("timed out"); }
    };
    const t = setTimeout(tick, 4000);
    return () => { alive = false; clearTimeout(t); };
  }, [job.job_id]);

  if (status === "completed" && assetId) {
    return (
      <div data-testid="alix-video-job-done">
        <ArtifactMedia art={{ kind: "video", url: `/api/central-core/vault-file/${assetId}`, title: job.title }} />
        <p className="px-3 py-2 font-mono text-[9px] uppercase tracking-widest text-gold/70 flex items-center gap-1.5"><Check size={11} strokeWidth={3} /> Saved to your Vault</p>
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div data-testid="alix-video-job-failed" className="px-3 py-4 flex items-center gap-2">
        <X size={15} className="text-red-400 shrink-0" />
        <p className="font-mono text-[11px] text-cream">Reel render hit a snag{err ? `: ${String(err).slice(0, 90)}` : ""}. Ask me “what went wrong” and I’ll diagnose it.</p>
      </div>
    );
  }
  return (
    <div data-testid="alix-video-job-processing" className="px-3 py-4 flex items-center gap-2">
      <Loader2 size={15} className="text-gold animate-spin shrink-0" />
      <p className="font-mono text-[11px] leading-relaxed text-cream">Directing your 30-second reel — chaining shots &amp; stitching. It’ll play here the moment it’s ready.</p>
    </div>
  );
}

// Renders a sent attachment (image thumb via authed blob, else a file chip).
function AlixAttachment({ att }) {
  const [src, setSrc] = useState(null);
  const isImg = (att.mime_type || "").startsWith("image/");
  const path = (att.url || "").replace(/^\/api/, "");
  useEffect(() => {
    let url;
    if (isImg && path) {
      api.get(path, { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
        .catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path, isImg]);

  if (isImg) {
    return src
      ? <img src={src} alt={att.name} data-testid="alix-attach-img" className="mt-1 w-28 h-28 object-cover border-2 border-charcoal rounded-lg" />
      : <div className="mt-1 w-28 h-28 grid place-items-center border-2 border-charcoal rounded-lg bg-charcoal"><Loader2 size={16} className="text-gold animate-spin" /></div>;
  }
  return (
    <div data-testid="alix-attach-file" className="mt-1 flex items-center gap-1.5 border-2 border-charcoal rounded-lg bg-white/70 px-2 py-1">
      <FileText size={13} className="text-charcoal" />
      <span className="font-mono text-[10px] text-charcoal truncate max-w-[140px]">{att.name}</span>
    </div>
  );
}

// Founder image→creative result card. Neo-Brutalist, mobile-first, copy per line.
function CreativeCards({ data, onCopy, onRegen, onSave, regenBusy, saved }) {
  const a = data.analysis || {};
  const o = data.outputs || {};
  const conf = Math.round((a.confidence || 0) * 100);
  const Line = ({ text, tid }) => (
    <div className="flex items-start gap-1.5 border-2 border-black bg-white px-2 py-1.5" data-testid={tid}>
      <p className="flex-1 font-mono text-[11px] leading-snug text-charcoal break-words">{text}</p>
      <button onClick={() => onCopy(text)} aria-label="Copy" data-testid="alix-creative-copy"
        className="shrink-0 grid h-6 w-6 place-items-center border-2 border-black bg-gold text-black active:translate-x-[1px] active:translate-y-[1px]"><Copy size={11} strokeWidth={3} /></button>
    </div>
  );
  const Section = ({ title, children }) => (
    <div className="space-y-1.5">
      <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-gold/80">{title}</p>
      {children}
    </div>
  );
  return (
    <div data-testid="alix-creative-card" className="mt-1.5 w-full max-w-[92%] border-2 border-black bg-charcoal shadow-[3px_3px_0px_#D4AF37]">
      <div className="flex items-center gap-2 border-b-2 border-black bg-gold px-3 py-1.5">
        <Sparkles size={13} className="text-black" strokeWidth={2.6} />
        <span className="flex-1 font-mono text-[9px] font-black uppercase tracking-[0.2em] text-black">Creative from Image</span>
        <span className="font-mono text-[8px] font-black uppercase text-black/60">{conf}% conf</span>
      </div>
      <div className="space-y-3 p-3">
        {/* analysis chips */}
        <div className="flex flex-wrap gap-1.5" data-testid="alix-creative-analysis">
          {[a.subject, a.mood, a.style].filter(Boolean).map((t, i) => (
            <span key={i} className="border-2 border-black bg-cream px-2 py-0.5 font-mono text-[9px] font-black uppercase tracking-wide text-charcoal">{t}</span>
          ))}
          {(a.palette || []).slice(0, 4).map((c, i) => (
            <span key={`p${i}`} className="border-2 border-black bg-white px-2 py-0.5 font-mono text-[9px] text-charcoal">{c}</span>
          ))}
        </div>
        {(o.captions || []).length > 0 && <Section title="Captions">{o.captions.map((t, i) => <Line key={i} text={t} tid={`alix-creative-caption-${i}`} />)}</Section>}
        {(o.hooks || []).length > 0 && <Section title="Hooks">{o.hooks.map((t, i) => <Line key={i} text={t} tid={`alix-creative-hook-${i}`} />)}</Section>}
        {(o.visual_direction || []).length > 0 && <Section title="Visual Direction">{o.visual_direction.map((t, i) => <Line key={i} text={t} tid={`alix-creative-vd-${i}`} />)}</Section>}
        {o.premium_version && <Section title="Premium"><Line text={o.premium_version} tid="alix-creative-premium" /></Section>}
        {o.bold_version && <Section title="Bold"><Line text={o.bold_version} tid="alix-creative-bold" /></Section>}
        <div className="flex gap-2 pt-1">
          <button onClick={onRegen} disabled={regenBusy} data-testid="alix-creative-regen"
            className="flex flex-1 items-center justify-center gap-1.5 border-2 border-black bg-cream px-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest text-charcoal shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
            {regenBusy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} strokeWidth={2.6} />} Regenerate
          </button>
          <button onClick={onSave} disabled={saved} data-testid="alix-creative-save"
            className="flex flex-1 items-center justify-center gap-1.5 border-2 border-black bg-gold px-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest text-black shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
            {saved ? <Check size={12} strokeWidth={3} /> : <Save size={12} strokeWidth={2.6} />} {saved ? "Saved" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Ready reel DRAFT card — preview + regenerate + save. NO publish/share CTA.
function ReelDraftCard({ draft, onRegen, onSave, regenBusy, saved }) {
  return (
    <div data-testid="alix-reel-draft-card" className="mt-1.5 w-full max-w-[92%] border-2 border-black bg-charcoal shadow-[3px_3px_0px_#D4AF37]">
      <div className="flex items-center gap-2 border-b-2 border-black bg-gold px-3 py-1.5">
        <Clapperboard size={13} className="text-black" strokeWidth={2.6} />
        <span className="flex-1 font-mono text-[9px] font-black uppercase tracking-[0.2em] text-black truncate">{draft.title || "Reel Draft"}</span>
        <span className="border-2 border-black bg-black px-1.5 py-0.5 font-mono text-[7px] font-black uppercase tracking-widest text-gold" data-testid="alix-reel-draft-badge">Draft</span>
      </div>
      {draft.asset_id && <ArtifactMedia art={{ kind: "video", url: `/api/central-core/vault-file/${draft.asset_id}`, title: draft.title }} />}
      <div className="space-y-2 p-3">
        <div className="flex items-center gap-2 font-mono text-[9px] font-black uppercase tracking-widest text-gold/70">
          <span>{draft.duration || 6}s</span>{draft.engine ? <span className="text-gold/40">· {String(draft.engine).replace(/_/g, " ")}</span> : null}
        </div>
        {draft.script && <p className="border-2 border-black bg-white px-2 py-1.5 font-mono text-[10px] leading-snug text-charcoal whitespace-pre-wrap break-words">{draft.script}</p>}
        <div className="flex gap-2 pt-0.5">
          <button onClick={onRegen} disabled={regenBusy} data-testid="alix-reel-regen"
            className="flex flex-1 items-center justify-center gap-1.5 border-2 border-black bg-cream px-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest text-charcoal shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
            {regenBusy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} strokeWidth={2.6} />} Regenerate
          </button>
          <button onClick={onSave} disabled={saved} data-testid="alix-reel-save"
            className="flex flex-1 items-center justify-center gap-1.5 border-2 border-black bg-gold px-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest text-black shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
            {saved ? <Check size={12} strokeWidth={3} /> : <Save size={12} strokeWidth={2.6} />} {saved ? "Saved to Vault" : "Save to Vault"}
          </button>
        </div>
      </div>
    </div>
  );
}

// In-chat reel PREP sheet (draft-only). Platform intent is advisory formatting
// guidance only — it NEVER triggers any publishing.
function ReelPrepSheet({ init, onClose, onGenerate, busy, stage }) {
  const [concept, setConcept] = useState(init.concept || "");
  const [duration, setDuration] = useState(6);
  const [tone, setTone] = useState("Cinematic");
  const [platform, setPlatform] = useState("Instagram");
  const [captions, setCaptions] = useState(true);
  const [vibe, setVibe] = useState("");
  const TONES = ["Cinematic", "Playful", "Bold", "Elegant"];
  const PLATS = ["Instagram", "TikTok", "YouTube"];
  const Chip = ({ active, onClick, children, tid }) => (
    <button onClick={onClick} data-testid={tid} className={`border-2 border-black px-2.5 py-1.5 font-mono text-[9px] font-black uppercase tracking-wide ${active ? "bg-gold text-black" : "bg-white text-charcoal"}`}>{children}</button>
  );
  return (
    <div className="absolute inset-0 z-[120] flex items-end bg-black/60" data-testid="alix-reel-sheet" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-h-[88%] w-full overflow-y-auto border-t-4 border-black bg-cream">
        <div className="sticky top-0 flex items-center justify-between border-b-2 border-black bg-charcoal px-3 py-2.5">
          <span className="flex items-center gap-1.5 font-mono text-[11px] font-black uppercase tracking-widest text-gold"><Clapperboard size={14} /> Turn Into Reel</span>
          <button onClick={onClose} data-testid="alix-reel-sheet-close" className="grid h-7 w-7 place-items-center border-2 border-gold bg-black text-gold"><X size={14} /></button>
        </div>
        <div className="space-y-3 p-3">
          <div>
            <p className="mb-1 font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/70">Concept</p>
            <textarea value={concept} onChange={(e) => setConcept(e.target.value)} rows={2} data-testid="alix-reel-concept" placeholder="A short concept, caption or hook…" className="w-full resize-none border-2 border-black bg-white p-2 font-mono text-[11px] text-charcoal outline-none" />
          </div>
          <div>
            <p className="mb-1 font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/70">Length</p>
            <div className="flex gap-2">{[5, 7, 10].map((d) => <Chip key={d} active={duration === d} onClick={() => setDuration(d)} tid={`alix-reel-dur-${d}`}>{d}s</Chip>)}</div>
          </div>
          <div>
            <p className="mb-1 font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/70">Tone</p>
            <div className="flex flex-wrap gap-2">{TONES.map((t) => <Chip key={t} active={tone === t} onClick={() => setTone(t)} tid={`alix-reel-tone-${t}`}>{t}</Chip>)}</div>
          </div>
          <div>
            <p className="mb-1 font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/70">Platform intent <span className="text-charcoal/40">· formatting hint only</span></p>
            <div className="flex flex-wrap gap-2">{PLATS.map((p) => <Chip key={p} active={platform === p} onClick={() => setPlatform(p)} tid={`alix-reel-plat-${p}`}>{p}</Chip>)}</div>
          </div>
          <div>
            <p className="mb-1 font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/70">Motion / audio vibe</p>
            <input value={vibe} onChange={(e) => setVibe(e.target.value)} data-testid="alix-reel-vibe" placeholder="e.g. slow push-in, energetic cuts" className="w-full border-2 border-black bg-white p-2 font-mono text-[11px] text-charcoal outline-none" />
          </div>
          <label className="flex cursor-pointer items-center gap-2 border-2 border-black bg-white p-2" data-testid="alix-reel-captions-row">
            <input type="checkbox" checked={captions} onChange={(e) => setCaptions(e.target.checked)} data-testid="alix-reel-captions" className="h-4 w-4 accent-black" />
            <span className="font-mono text-[10px] font-black uppercase tracking-wide text-charcoal">Bake on-screen caption</span>
          </label>
          <button onClick={() => onGenerate({ concept, duration, tone, platform_intent: platform, include_captions: captions, vibe })} disabled={busy}
            data-testid="alix-reel-generate" className="flex w-full items-center justify-center gap-2 border-2 border-black bg-charcoal py-3 font-mono text-sm font-black uppercase tracking-widest text-gold shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
            {busy ? <><Loader2 size={15} className="animate-spin" /> {stage || "Working…"}</> : <><Clapperboard size={15} /> Generate Reel Draft</>}
          </button>
          <p className="text-center font-mono text-[8px] uppercase tracking-widest text-charcoal/40">Draft only — stays private in your Vault until you decide</p>
        </div>
      </div>
    </div>
  );
}

export default function AlixChat({ onClose, onDirective, onOpenDocReader, onOpenLedger }) {
  const { user } = useSession();
  const isFounder = !!user && (user.role === "founder" || (user.handle || "").toLowerCase() === "johnnywalker" || (user.email || "").toLowerCase() === "djohnm1972@gmail.com");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [attach, setAttach] = useState(null);
  const [vaultPicks, setVaultPicks] = useState([]);
  const [showVault, setShowVault] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [savedIds, setSavedIds] = useState(() => new Set());
  const [savingId, setSavingId] = useState(null);
  const [creativeBusy, setCreativeBusy] = useState(false);
  const [creativeSaved, setCreativeSaved] = useState(() => new Set());
  const lastSrcRef = useRef(null);
  const varRef = useRef(0);
  const [reelSheet, setReelSheet] = useState(null); // {src, concept}
  const [reelBusy, setReelBusy] = useState(false);
  const [reelStage, setReelStage] = useState("");
  const [reelSaved, setReelSaved] = useState(() => new Set());
  const reelSrcRef = useRef(null);
  const reelFormRef = useRef(null);
  const reelVarRef = useRef(0);
  const sessionRef = useRef(localStorage.getItem(SESSION_KEY) || null);
  const scrollRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const fileRef = useRef(null);

  // Chat surfaces suppress the floating "+" FAB and the founder error badge.
  useEffect(() => {
    window.__mistChatSurface = true;
    window.dispatchEvent(new CustomEvent("mist:chat-surface", { detail: { active: true } }));
    return () => {
      window.__mistChatSurface = false;
      window.dispatchEvent(new CustomEvent("mist:chat-surface", { detail: { active: false } }));
    };
  }, []);

  useEffect(() => {
    // Cross-device continuity: load the user's most recent conversation from the
    // server (Mongo) — so a chat started on the computer shows on mobile and
    // vice-versa. We adopt the server's session_id instead of trusting only
    // this device's localStorage (which is per-device and caused the mismatch).
    api.get("/alix/history/latest")
      .then((r) => {
        if (r.data.session_id) {
          sessionRef.current = r.data.session_id;
          localStorage.setItem(SESSION_KEY, r.data.session_id);
        }
        setMessages(r.data.messages || []);
      })
      .catch(() => {
        if (sessionRef.current) {
          api.get("/alix/history", { params: { session_id: sessionRef.current } })
            .then((r) => setMessages(r.data.messages || [])).catch(() => {});
        }
      });
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const copy = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("copy failed");
      }
      toast.success("Copied");
    } catch { toast.error("Copy failed"); }
  };

  // Phase 26b — persist an inline-generated artifact to the vault (fresh asset_id).
  const saveToVault = async (m) => {
    if (!m.artifact || savedIds.has(m.id) || savingId) return;
    setSavingId(m.id);
    try {
      await api.post("/alix/save-to-vault", {
        content: m.artifact.content, kind: m.artifact.kind, title: m.artifact.title,
      });
      setSavedIds((s) => new Set(s).add(m.id));
      toast.success("Saved to Vault");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingId(null); }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED.includes(ext)) { toast.error("Unsupported file type"); return; }
    if (f.size > sizeLimit(ext)) { toast.error(`File too large (max ${Math.round(sizeLimit(ext) / (1024 * 1024))} MB)`); return; }
    setAttach({ file: f, name: f.name, size: f.size, isImage: f.type.startsWith("image/"), url: URL.createObjectURL(f) });
  };

  // FOUNDER — resolve the current image source (uploaded file OR vault pick).
  const resolveImageSource = async () => {
    if (attach && attach.isImage) {
      const fd = new FormData(); fd.append("file", attach.file);
      const { data } = await api.post("/alix/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return { file_id: data.file_id, name: data.name, url: data.url, thumb: attach.url };
    }
    const vimg = vaultPicks.find((p) => p.kind === "image" || IMG_EXT_RE.test(p.name || ""));
    if (vimg) return { asset_id: vimg.asset_id, name: vimg.name, url: vimg.url };
    return null;
  };

  const runCreative = async (src, variation) => {
    setCreativeBusy(true);
    try {
      const body = src.file_id ? { file_id: src.file_id, variation } : { asset_id: src.asset_id, variation };
      const { data } = await api.post("/alix/creative-from-image", body);
      setMessages((m) => [...m, { id: `c-${Date.now()}`, role: "assistant", creative: data, src }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create from that image");
    } finally { setCreativeBusy(false); }
  };

  const createFromImage = async () => {
    if (creativeBusy || busy) return;
    let src = null;
    try { src = await resolveImageSource(); } catch { toast.error("Upload failed"); return; }
    if (!src) { toast.error("Attach or pick an image first, then tap Create"); return; }
    lastSrcRef.current = src; varRef.current = 0;
    setMessages((m) => [...m, { id: `uc-${Date.now()}`, role: "user", text: "Create better content from this image", attachments: src.file_id ? [{ name: src.name, mime_type: "image/*", url: src.url }] : [{ asset_id: src.asset_id, kind: "image", name: src.name, url: src.url }] }]);
    clearAttach(); setVaultPicks([]);
    await runCreative(src, 0);
  };

  const regenCreative = async () => {
    if (!lastSrcRef.current || creativeBusy) return;
    varRef.current += 1;
    await runCreative(lastSrcRef.current, varRef.current);
  };

  const saveCreative = async (m) => {
    if (creativeSaved.has(m.id)) return;
    const o = m.creative?.outputs || {};
    const body = [
      "PREMIUM:\n" + (o.premium_version || ""),
      "\nBOLD:\n" + (o.bold_version || ""),
      "\nCAPTIONS:\n" + (o.captions || []).map((c) => "• " + c).join("\n"),
      "\nHOOKS:\n" + (o.hooks || []).map((c) => "• " + c).join("\n"),
    ].join("\n");
    try {
      await api.post("/alix/save-to-vault", { content: body, kind: "text", title: "Creative from Image" });
      setCreativeSaved((s) => new Set(s).add(m.id));
      toast.success("Saved to Vault");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  // ── TURN INTO REEL (draft-only) ──────────────────────────────────────────
  const openReelSheet = (m) => {
    const o = m.creative?.outputs || {};
    const concept = o.premium_version || (o.captions && o.captions[0]) || "";
    setReelSheet({ src: m.src || null, concept });
  };

  const runReelDraft = async (form, src, variation) => {
    setReelBusy(true); setReelStage("Planning…");
    try {
      const body = {
        source_type: src?.asset_id ? "vault_asset" : (src?.file_id ? "image" : "text"),
        asset_id: src?.asset_id || null, file_id: src?.file_id || null,
        concept: form.concept, duration: form.duration, tone: form.tone,
        platform_intent: form.platform_intent, include_captions: form.include_captions,
        vibe: form.vibe, variation,
      };
      const { data } = await api.post("/alix/reel-draft", body);
      const STAGES = { queued: "Queued…", planning: "Planning…", imagining: "Imagining…", generating: "Generating…", lettering: "Adding caption…", ready: "Finishing…" };
      const job = await pipelinesApi.poll(data.job_id, { interval: 3000, timeout: 300000, onTick: (j) => { if (j?.stage) setReelStage(STAGES[j.stage] || "Working…"); } });
      if (job.status !== "completed" || !job.asset_id) throw new Error(job.error || "Reel draft failed");
      setMessages((mm) => [...mm, { id: `r-${Date.now()}`, role: "assistant", reelDraft: { asset_id: job.asset_id, title: job.title, script: job.script, duration: job.duration, draft_id: job.draft_id, engine: job.engine } }]);
      setReelSheet(null);
      toast.success("Reel draft ready ✓ saved to your Vault");
    } catch (e) {
      console.error("reel-draft failed", e);
      toast.error(e?.response?.data?.detail || e.message || "Couldn't create that reel draft");
    } finally { setReelBusy(false); setReelStage(""); }
  };

  const generateReel = async (form) => {
    reelSrcRef.current = reelSheet?.src || null;
    reelFormRef.current = form; reelVarRef.current = 0;
    await runReelDraft(form, reelSrcRef.current, 0);
  };

  const regenReel = async () => {
    if (!reelFormRef.current || reelBusy) return;
    reelVarRef.current += 1;
    await runReelDraft(reelFormRef.current, reelSrcRef.current, reelVarRef.current);
  };

  const saveReelDraft = async (m) => {
    if (reelSaved.has(m.id)) return;
    const d = m.reelDraft || {};
    const body = `REEL DRAFT: ${d.title || ""}\nDuration: ${d.duration || 6}s\n\n${d.script || ""}`;
    try {
      await api.post("/alix/save-to-vault", { content: body, kind: "text", title: (d.title || "Reel Draft") + " — plan" });
      setReelSaved((s) => new Set(s).add(m.id));
      toast.success("Saved to Vault");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const clearAttach = () => {
    if (attach?.url) URL.revokeObjectURL(attach.url);
    setAttach(null);
  };

  const send = async () => {
    const text = input.trim();
    if ((!text && !attach && vaultPicks.length === 0) || busy) return;
    setBusy(true);
    try {
      let uploaded = null;
      if (attach) {
        const fd = new FormData();
        fd.append("file", attach.file);
        const { data } = await api.post("/alix/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded = data; // {file_id, name, mime_type, size, url}
      }
      // Vault picks travel as {asset_id, kind, name}; uploaded file as {file_id,...}.
      const outAtt = [
        ...vaultPicks.map((p) => ({ asset_id: p.asset_id, kind: p.kind, name: p.name, url: p.url })),
        ...(uploaded ? [uploaded] : []),
      ];
      setInput("");
      clearAttach();
      setVaultPicks([]);
      setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", text, attachments: outAtt }]);
      const { data } = await api.post("/alix/chat", { message: text, session_id: sessionRef.current, attachments: outAtt });
      sessionRef.current = data.session_id;
      localStorage.setItem(SESSION_KEY, data.session_id);
      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", text: data.reply, directive: data.directive || null, artifact: data.artifact || null, credit_block: data.credit_block || null }]);
      if (data.artifact && (data.artifact.kind === "image" || data.artifact.kind === "video" || data.artifact.kind === "music_job")) {
        window.dispatchEvent(new Event("mist:coins-updated"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ALIX is unavailable");
      setMessages((m) => [...m, { id: `e-${Date.now()}`, role: "assistant", text: "⚠️ I couldn't reach the core just now. Try again in a moment." }]);
    } finally { setBusy(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "alix_voice.webm");
          const { data } = await api.post("/vault-security/blueprint/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data?.transcript) setInput((v) => (v ? v + " " : "") + data.transcript);
          else toast.error("No speech detected");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription unavailable");
        } finally { setTranscribing(false); }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stopRec = () => { recRef.current?.stop(); setRecording(false); };

  return (
    <div className="h-full w-full max-w-[412px] mx-auto flex flex-col overflow-hidden bg-charcoal" data-testid="alix-chat">
      {/* Header — X close + mic (top-left), ALIX identifier (top-right) */}
      <header className="shrink-0 flex items-center justify-between px-3 py-3 border-b-2 border-gold/40">
        <div className="flex items-center gap-2">
          <button onClick={onClose} data-testid="alix-close-btn" aria-label="Close" className="w-9 h-9 grid place-items-center border-2 border-gold bg-charcoal text-gold shadow-[2px_2px_0px_#D4AF37] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><X size={17} strokeWidth={2.6} /></button>
          <button onClick={recording ? stopRec : startRec} disabled={transcribing} data-testid="alix-mic-btn" aria-label="Voice" className={`w-9 h-9 grid place-items-center border-2 border-charcoal shadow-[2px_2px_0px_#D4AF37] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${recording ? "bg-red-500 text-white animate-pulse" : "bg-gold text-black"}`}>
            {transcribing ? <Loader2 size={16} className="animate-spin" /> : recording ? <Square size={15} strokeWidth={3} /> : <Mic size={16} strokeWidth={2.4} />}
          </button>
          {onOpenDocReader && (
            <button onClick={onOpenDocReader} data-testid="alix-doc-reader-btn" aria-label="Doc Reader"
              className="w-9 h-9 grid place-items-center border-2 border-gold bg-charcoal text-gold shadow-[2px_2px_0px_#D4AF37] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
              <FileText size={16} strokeWidth={2.4} />
            </button>
          )}
        </div>
        <span className="font-mono text-xl font-black tracking-[0.28em] text-gold" data-testid="alix-title">ALIX</span>
      </header>

      {/* Transcript — scrollbar hidden */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-3" data-testid="alix-transcript">
        <LedgerAlertHeading onManage={onOpenLedger} />
        {messages.length === 0 && !busy && (
          <div className="mt-10 text-center" data-testid="alix-empty">
            <p className="mt-3 font-mono text-[12px] text-white/60 leading-relaxed px-6">Hey, I'm ALIX. Ask me anything, or tell me what you want to create.</p>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
            <div
              data-testid={`alix-msg-${m.role}`}
              className={[
                "relative max-w-[80%] px-3.5 py-2.5 border-2 border-charcoal font-mono text-[13px] leading-relaxed whitespace-pre-wrap break-words select-text",
                m.role === "user"
                  ? "bg-gold text-black rounded-2xl rounded-br-sm shadow-[3px_3px_0px_#000]"
                  : "bg-cream text-charcoal rounded-2xl rounded-bl-sm shadow-[3px_3px_0px_#D4AF37] pb-7",
              ].join(" ")}
            >
              {m.text}
              {(m.attachments || []).map((a) => <AlixAttachment key={a.file_id || a.name} att={a} />)}
              {m.role === "assistant" && m.text && (
                <button onClick={() => copy(m.text)} data-testid="alix-copy-btn" aria-label="Copy"
                  className="absolute bottom-1.5 right-1.5 flex items-center gap-1 border-2 border-charcoal bg-gold text-black font-mono text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded shadow-[1.5px_1.5px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
                  <Copy size={9} strokeWidth={3} /> Copy
                </button>
              )}
            </div>
            {m.role === "assistant" && m.directive && (
              <button
                onClick={() => onDirective?.(m.directive)}
                data-testid="alix-directive-launch"
                className="mt-1.5 max-w-[80%] flex items-center gap-2 border-2 border-gold bg-charcoal text-gold rounded-xl px-3 py-2 shadow-[3px_3px_0px_#D4AF37] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none text-left"
              >
                <span className="shrink-0 w-7 h-7 grid place-items-center border-2 border-gold rounded-lg bg-gold/10">
                  <Wand2 size={14} strokeWidth={2.6} />
                </span>
                <span className="min-w-0">
                  <span className="block font-mono text-[8px] font-black uppercase tracking-[0.2em] text-gold/60">AI Director</span>
                  <span className="block font-mono text-[11px] font-black uppercase tracking-widest truncate">Open {m.directive.label}</span>
                </span>
              </button>
            )}
            {m.role === "assistant" && m.credit_block && (
              <button onClick={() => setShowPlans(true)} data-testid="alix-topup-btn"
                className="mt-1.5 max-w-[80%] flex items-center gap-2 border-2 border-gold bg-gold text-black rounded-xl px-3 py-2 shadow-[3px_3px_0px_#D4AF37] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                <Coins size={15} strokeWidth={2.6} />
                <span className="font-mono text-[11px] font-black uppercase tracking-widest">Buy Credits</span>
              </button>
            )}
            {m.role === "assistant" && m.artifact && (
              <div data-testid="alix-artifact" className="mt-1.5 max-w-[85%] border-2 border-gold bg-charcoal rounded-xl overflow-hidden shadow-[3px_3px_0px_#D4AF37]">
                <div className="flex items-center gap-2 px-3 py-2 border-b-2 border-gold/40 bg-gold/10">
                  <ScrollText size={13} className="text-gold shrink-0" />
                  <span className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-gold truncate flex-1">{m.artifact.title}</span>
                  <span className="font-mono text-[7px] font-black uppercase tracking-widest text-gold/50 shrink-0">{m.artifact.kind}</span>
                </div>
                {m.artifact.kind === "poem" ? (
                  <>
                    <p className="px-3 py-2.5 font-mono text-[12px] leading-relaxed text-cream whitespace-pre-wrap select-text">{m.artifact.content}</p>
                    <div className="px-3 pb-3">
                      <button onClick={() => saveToVault(m)} disabled={savedIds.has(m.id) || savingId === m.id} data-testid="alix-save-vault"
                        className="w-full flex items-center justify-center gap-2 border-2 border-charcoal bg-gold text-black font-mono text-[10px] font-black uppercase tracking-widest px-3 py-2 rounded-lg shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
                        {savingId === m.id ? <Loader2 size={13} className="animate-spin" /> : savedIds.has(m.id) ? <Check size={13} strokeWidth={3} /> : <Save size={13} strokeWidth={2.6} />}
                        {savedIds.has(m.id) ? "Saved to Vault" : "Save to Vault"}
                      </button>
                    </div>
                  </>
                ) : m.artifact.kind === "music_job" ? (
                  <div className="px-3 py-4 flex items-center gap-2">
                    <Loader2 size={15} className="text-gold animate-spin shrink-0" />
                    <p className="font-mono text-[11px] leading-relaxed text-cream">Producing your track — it'll appear in your Music library shortly.</p>
                  </div>
                ) : m.artifact.kind === "video_job" ? (
                  <VideoJobArtifact job={m.artifact} />
                ) : (
                  <>
                    <ArtifactMedia art={m.artifact} />
                    <p className="px-3 py-2 font-mono text-[9px] uppercase tracking-widest text-gold/70 flex items-center gap-1.5"><Check size={11} strokeWidth={3} /> Saved to your Vault</p>
                  </>
                )}
              </div>
            )}
            {m.role === "assistant" && m.creative && (
              <>
                <CreativeCards data={m.creative} onCopy={copy} onRegen={regenCreative} onSave={() => saveCreative(m)} regenBusy={creativeBusy} saved={creativeSaved.has(m.id)} />
                <button onClick={() => openReelSheet(m)} data-testid="alix-reel-turn"
                  className="mt-1.5 w-full max-w-[92%] flex items-center justify-center gap-2 border-2 border-black bg-gold text-black font-mono text-[10px] font-black uppercase tracking-widest px-3 py-2.5 shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                  <Clapperboard size={14} strokeWidth={2.6} /> Turn Into Reel
                </button>
              </>
            )}
            {m.role === "assistant" && m.reelDraft && (
              <ReelDraftCard draft={m.reelDraft} onRegen={regenReel} onSave={() => saveReelDraft(m)} regenBusy={reelBusy} saved={reelSaved.has(m.id)} />
            )}
          </div>
        ))}
        {(busy || creativeBusy) && (
          <div className="flex justify-start" data-testid="alix-typing">
            <div className="bg-cream text-charcoal rounded-2xl rounded-bl-sm border-2 border-charcoal px-3.5 py-2.5 shadow-[3px_3px_0px_#D4AF37] flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /><span className="font-mono text-[12px]">{creativeBusy ? "ALIX is studying your image…" : "ALIX is thinking…"}</span>
            </div>
          </div>
        )}
      </div>

      {/* Vault-picked assets (chips) — shown above the input before send */}
      {vaultPicks.length > 0 && (
        <div className="shrink-0 px-3 pt-2 flex flex-wrap gap-1.5" data-testid="alix-vault-chips">
          {vaultPicks.map((p) => (
            <span key={p.asset_id} className="flex items-center gap-1 border-2 border-gold bg-charcoal rounded-lg px-2 py-1">
              <span className="font-mono text-[9px] uppercase tracking-wide text-gold/80 truncate max-w-[90px]">{p.kind}·{p.name}</span>
              <button onClick={() => setVaultPicks((v) => v.filter((x) => x.asset_id !== p.asset_id))} aria-label="Remove" className="text-gold active:scale-90"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}

      {/* Attachment preview chip — shown above the input before send */}
      {attach && (
        <div className="shrink-0 px-3 pt-2" data-testid="alix-attach-chip">
          <div className="flex items-center gap-2 border-2 border-gold bg-charcoal rounded-xl px-2 py-1.5">
            {attach.isImage
              ? <img src={attach.url} alt="" className="w-9 h-9 object-cover border-2 border-gold rounded" />
              : <span className="w-9 h-9 grid place-items-center border-2 border-gold rounded"><FileText size={16} className="text-gold" /></span>}
            <div className="flex-1 min-w-0 leading-tight">
              <p className="font-mono text-[11px] text-white truncate">{attach.name}</p>
              <p className="font-mono text-[8px] uppercase tracking-widest text-gold/70">{Math.max(1, Math.round(attach.size / 1024))} KB</p>
            </div>
            <button onClick={clearAttach} data-testid="alix-attach-remove" aria-label="Remove attachment" className="w-7 h-7 grid place-items-center border-2 border-gold text-gold rounded active:scale-90"><X size={14} /></button>
          </div>
        </div>
      )}

      {/* Footer entry node — paperclip + vault + input + solid Send */}
      <footer className="shrink-0 flex items-center gap-2 px-3 py-3 border-t-2 border-gold/40">
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="alix-attach-btn" aria-label="Attach file"
          className="shrink-0 w-10 h-10 grid place-items-center border-2 border-gold bg-gold text-black rounded-xl shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
          <Paperclip size={17} strokeWidth={2.4} />
        </button>
        <button onClick={() => setShowVault(true)} disabled={busy} data-testid="alix-vault-btn" aria-label="Attach from Vault"
          className="shrink-0 w-10 h-10 grid place-items-center border-2 border-gold bg-charcoal text-gold rounded-xl shadow-[2px_2px_0px_#D4AF37] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
          <Library size={17} strokeWidth={2.4} />
        </button>
        {isFounder && (
          <button onClick={createFromImage} disabled={busy || creativeBusy} data-testid="alix-create-from-image" aria-label="Create from image"
            title="Founder: create captions, hooks & concepts from an attached image"
            className="shrink-0 w-10 h-10 grid place-items-center border-2 border-black bg-gold text-black rounded-xl shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
            {creativeBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={17} strokeWidth={2.4} />}
          </button>
        )}
        <input ref={fileRef} type="file" hidden data-testid="alix-file-input"
          accept=".jpg,.jpeg,.png,.webp,.pdf,.txt,.heic,.mp4,.mov,.webm,.m4v,.mp3,.wav,.m4a,.aac,.ogg,.flac,image/*,video/*,audio/*,application/pdf,text/plain" onChange={pickFile} />
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Enter prompt…"
          data-testid="alix-input"
          className="flex-1 min-w-0 bg-cream border-2 border-gold rounded-xl px-3 py-2.5 font-mono text-[13px] text-charcoal outline-none placeholder:text-charcoal/40"
        />
        <button onClick={send} disabled={busy || (!input.trim() && !attach && vaultPicks.length === 0)} data-testid="alix-send"
          className="shrink-0 flex items-center gap-1 border-2 border-charcoal bg-charcoal text-gold font-mono text-[11px] font-black uppercase tracking-widest px-3.5 py-2.5 rounded-xl shadow-[3px_3px_0px_#D4AF37] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <ArrowUp size={15} strokeWidth={2.6} />} Send
        </button>
      </footer>

      {showVault && (
        <VaultPicker endpoint="/alix/context/vault" onClose={() => setShowVault(false)}
          onPick={(picks) => setVaultPicks((v) => {
            const map = {}; [...v, ...picks].forEach((p) => { map[p.asset_id] = p; }); return Object.values(map);
          })} />
      )}
      {showPlans && <PlansPanel onBack={() => setShowPlans(false)} />}
      {reelSheet && (
        <ReelPrepSheet init={reelSheet} onClose={() => !reelBusy && setReelSheet(null)} onGenerate={generateReel} busy={reelBusy} stage={reelStage} />
      )}
    </div>
  );
}
